import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { UserRoles, UserRolesEnum, UserType } from "../Constant";

interface AuthContextType {
  user: UserType | null;
  userPermission: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (data: any) => void;
  signOut: () => void;
  navigationPath: any;
  checkUserPermission: (feature: string) => boolean;
  isAdmin: boolean;
}

const defaultContext: AuthContextType = {
  user: null,
  userPermission: null,
  isAuthenticated: false,
  navigationPath: "none",

  isLoading: true,
  signIn: () => {},
  signOut: () => {},
  checkUserPermission: () => false,
  isAdmin: false,
};

const AuthContext = createContext<AuthContextType>(defaultContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [userPermission, setUserPermission] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [navigationPath, setNavigationPath] = useState<"none" | "admin">(
    "none",
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    const isAuth = localStorage.getItem("isAuth");
    const userId = localStorage.getItem("userId");
    const username = localStorage.getItem("username");
    const storedRole = localStorage.getItem("userRole") as UserRoles | null;
    const permission = localStorage.getItem("userPermission");

    if (isAuth && storedRole) {
      const userRole: UserRoles = Object.values(UserRolesEnum).includes(
        storedRole as UserRolesEnum,
      )
        ? (storedRole as UserRoles)
        : UserRolesEnum.none;

      const newUser: UserType = {
        userId: userId || "",
        username: username || "",
        userRole,
      };

      setUser(newUser);
      setIsAuthenticated(true);
      setUserPermission(permission ? JSON.parse(permission) : []);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (
      user?.userRole === UserRolesEnum.admin ||
      user?.userRole === UserRolesEnum.cashier ||
      user?.userRole === UserRolesEnum.manager
    ) {
      setNavigationPath("admin");
    } else {
      setNavigationPath("none");
    }

    if (user?.userRole == UserRolesEnum.admin) {
      setIsAdmin(true);
    }
  }, [user]);

  const signOut = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    setUserPermission(null);
  }, []);

  const signIn = useCallback((data: any) => {
    const { isAuthDone, userData, userPermission } = data;

    localStorage.setItem("isAuth", String(isAuthDone));
    localStorage.setItem("userId", userData?.userId);
    localStorage.setItem("username", userData?.username);
    localStorage.setItem("userRole", userData?.userRole);
    localStorage.setItem("userPermission", JSON.stringify(userPermission));

    setUser({
      userId: userData?.userId,
      username: userData?.username,
      userRole: userData?.userRole,
    });

    if (userData?.userRole === UserRolesEnum.admin) {
      setIsAdmin(true);
    }

    setIsAuthenticated(true);
    setUserPermission(userPermission);
  }, []);

  const checkUserPermission = useCallback(
    (feature: string): boolean => {
      if (!userPermission || !Array.isArray(userPermission)) return false;

      return userPermission.some((category) =>
        category.features?.some(
          (perm: { navigation: string; is_locked?: number }) =>
            perm.navigation == feature && perm.is_locked == 0,
        ),
      );
    },
    [userPermission],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        navigationPath,
        userPermission,
        isAuthenticated,
        isLoading,
        signIn,
        signOut,
        isAdmin,
        checkUserPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthChecker = () => useContext(AuthContext);
